/* eslint-disable import/no-internal-modules */
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, split } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { RestLink } from 'apollo-link-rest';
import type { GraphQLError } from 'graphql';
import _ from 'lodash';

import { useMemo } from 'react';
import { store } from './factory';

export const Provider = ({ children }: { children?: React.ReactNode }): React.ReactElement => {
  const { environment, token, webplayerToken } = store((v) => _.pick(v, ['environment', 'token', 'webplayerToken']));

  const apollo = useMemo(() => {

    const httpLink = new HttpLink({
      uri: `${environment.GRAPHQL_URI}/graphql`
    });

    const wsLink = new WebSocketLink({
      uri: environment.WEBSOCKET_URI,
      options: {
        reconnect: true
      }
    });

    const restLink = new RestLink({
      uri: `${environment.WEBPLAYER_CONTROL_URI}/api/v1/`,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      customFetch:  async (uri, options) =>
       fetch(uri, {
          ...options,
          headers: {
            ...options.headers,
            authorization: webplayerToken ?? ''
          }
        })
    });

    const errorLink = onError(({ graphQLErrors, networkError }: ErrorResponse) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }: GraphQLError): void => {
          // eslint-disable-next-line no-console
          console.error(
            `[GraphQL Error]: Message: ${message}, Location: ${JSON.stringify(
              locations
            )}, Path: ${JSON.stringify(path)}`
          );
        });
      }

      if (networkError) {
        // eslint-disable-next-line no-console
        console.error(`[Network error]: ${networkError.message}`);
      }
    });

    const authLink = new ApolloLink((operation, forward) => {
      // Use the setContext method to set the HTTP headers.
      operation.setContext({
        headers: {
          authorization: token ? `Bearer ${token.jwt}` : ''
        }
      });

      return forward(operation);
    });

    const splitLink = split(({ query }) => {
      const definition = getMainDefinition(query);

      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    }, wsLink, httpLink);

    const networkLink = ApolloLink.from([
      errorLink,
      authLink,
      restLink,
      splitLink
    ]);

    return new ApolloClient({
      link: networkLink,
      cache: new InMemoryCache({
        typePolicies: {
          AssetWithEvent: {
            keyFields: ['eid'],
            fields: {
              /*
               * `client` carries no id, so the cache can't normalize it — it's an
               * embedded object, and by default an incoming one REPLACES the stored
               * one instead of merging. Queries select different client fields (the
               * events list wants cid + timezone, the sidebar's favorites feed and
               * the My Events stats query ask for less), so whichever wrote last
               * dropped the fields it didn't request. The events list then couldn't
               * be read from the cache, went back to the network for page 1, and
               * that reset pagination into a 1,2,1,2 fetch loop. Shallow-merge
               * instead, so a lean query only updates what it actually asked for.
               */
              client: { merge: true }
            }
          },
          Query: {
            fields: {
              getEvents: {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                keyArgs: (variables: Record<string, Record<string, unknown | undefined>> | null) => {
                  const excludedAttributes = new Set(['page', 'limit', 'ids', 'ignore_pagination', 'has_more']);
                  const acceptableArgs = (variables?.input ? Object.keys(variables.input) : []).filter(key => !excludedAttributes.has(key));

                  return ['input', acceptableArgs];
                },
                merge(
                  existing: { result: { __ref: string; eid: string }[] } | undefined,
                  incoming: { result: { __ref: string; eid: string }[] }
                ): { result: { __ref: string }[] } {

                  const existingMap = new Map(existing?.result.map((v) => [v.__ref, v]));
                  const incomingMap = new Map(incoming.result.map((v) => [v.__ref, v]));

                  const result: { __ref: string; eid: string }[] = [];

                  existingMap.forEach((value, key) => {
                    if (incomingMap.has(key)) {
                      result.push(incomingMap.get(key)!);
                      incomingMap.delete(key);
                    } else {
                      result.push(value);
                    }
                  });

                  result.push(...incomingMap.values());

                  return { ...incoming, result };
                }
              }
            }
          }
        }
      })
    });
  }, [environment.GRAPHQL_URI, environment.WEBPLAYER_CONTROL_URI, environment.WEBSOCKET_URI, token, webplayerToken]);

  return <ApolloProvider client={apollo}>{children}</ApolloProvider>;
};
